"use client";

import { c as _c } from "react/compiler-runtime";
import FolderOpen from "@mui/icons-material/FolderOpen";
import MenuIcon from "@mui/icons-material/Menu";
import Settings from "@mui/icons-material/Settings";
import { alpha, AppBar, type AppBarProps, Avatar, Box, Button, Container, IconButton, Menu, MenuItem, Link as MuiLink, Skeleton, Stack, Toolbar, type ToolbarProps, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { signIn, useSession } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useSnackbar } from "notistack";
import React, { type MouseEvent, useState } from "react";
import { MOBILE_APPBAR_HEIGHT } from "#/features/appBar/constants";
import { selectIsAppBarScrolled } from "#/features/appBar/model/appBarSlice";
import { SidePanel } from "#/features/menuSidePanel/ui/SidePanel";
import { useMobilePlaygroundView } from "#/features/playground/hooks/useMobilePlaygroundView";
import { usePlaygroundMobileLayout } from "#/features/playground/hooks/usePlaygroundMobileLayout";
import { useOptionalProjectBrowserContext } from "#/features/project/ui/ProjectBrowser/ProjectBrowserContext";
import { appFontStackDisplay } from "#/shared/fonts/fontVariables";
import { useI18nContext, useRoutePathname } from "#/shared/hooks";
import { useProfileImageUploader } from "#/shared/hooks";
import { useHasMounted } from "#/shared/hooks/useHasMounted";
import { getImageUrl } from "#/shared/lib";
import { useAppSelector } from "#/store/hooks";
const AVATAR_PLACEHOLDER = "/avatars/placeholder.png";
export type MainAppBarProps = {
  appBarVariant?: AppBarProps["variant"];
  toolbarVariant?: ToolbarProps["variant"];
  position?: AppBarProps["position"];
};
const LogoBrand: React.FC<{
  alt: string;
  showName?: boolean;
  logoSize?: number;
}> = t0 => {
  const $ = _c(10);
  const {
    alt,
    showName: t1,
    logoSize: t2
  } = t0;
  const showName = t1 === undefined ? true : t1;
  const logoSize = t2 === undefined ? 32 : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      display: "flex",
      alignItems: "center",
      textDecoration: "none",
      color: "inherit",
      mr: 1
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      display: "flex",
      alignItems: "center",
      gap: 0.5
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== alt || $[3] !== logoSize) {
    t5 = <Image alt={alt} src="/android-chrome-192x192.png" width={logoSize} height={logoSize} />;
    $[2] = alt;
    $[3] = logoSize;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== showName) {
    t6 = showName && <Typography variant="h6" noWrap={true} sx={{
      fontFamily: appFontStackDisplay,
      fontWeight: 700,
      letterSpacing: "-0.04em"
    }}>dStruct</Typography>;
    $[5] = showName;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t5 || $[8] !== t6) {
    t7 = <MuiLink component={Link} href="/" data-testid="app-bar-home-link" sx={t3}><Box sx={t4}>{t5}{t6}</Box></MuiLink>;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
};
export const MainAppBar: React.FC<MainAppBarProps> = t0 => {
  const $ = _c(77);
  const {
    appBarVariant: t1,
    toolbarVariant: t2,
    position: t3
  } = t0;
  const appBarVariant = t1 === undefined ? "elevation" : t1;
  const toolbarVariant = t2 === undefined ? "dense" : t2;
  const position = t3 === undefined ? "sticky" : t3;
  const currentPath = useRoutePathname();
  const theme = useTheme();
  const isPlaygroundMobileLayout = usePlaygroundMobileLayout();
  let t4;
  if ($[0] !== theme.breakpoints) {
    t4 = theme.breakpoints.down("lg");
    $[0] = theme.breakpoints;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const useCompactNav = useMediaQuery(t4);
  const {
    LL
  } = useI18nContext();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const hasMounted = useHasMounted();
  const router = useRouter();
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [anchorElNav, setAnchorElNav] = useState(null);
  const projectBrowser = useOptionalProjectBrowserContext();
  const {
    currentView
  } = useMobilePlaygroundView();
  const isScrolled = useAppSelector(selectIsAppBarScrolled);
  const session = useSession();
  let t5;
  if ($[2] !== currentPath) {
    t5 = currentPath.startsWith("/playground");
    $[2] = currentPath;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const isPlayground = t5;
  const useMobilePlayground = isPlayground && isPlaygroundMobileLayout;
  const pages = [{
    name: LL.PLAYGROUND(),
    href: "/playground"
  }, {
    name: LL.DAILY_PROBLEM_NAV(),
    href: "/daily"
  }] as const;
  useProfileImageUploader(session);
  let t6;
  if ($[4] !== LL || $[5] !== enqueueSnackbar || $[6] !== useMobilePlayground) {
    t6 = async () => {
      ;
      try {
        await signIn();
      } catch (t7) {
        const error = t7;
        if (useMobilePlayground) {
          const message = error instanceof Error ? error.message : LL.SIGN_IN_FAILED();
          enqueueSnackbar(message, {
            variant: "error"
          });
        }
      }
    };
    $[4] = LL;
    $[5] = enqueueSnackbar;
    $[6] = useMobilePlayground;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleSignIn = t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = event => {
      setAnchorElNav(event.currentTarget);
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleOpenNavMenu = t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setAnchorElNav(null);
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const handleCloseNavMenu = t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setIsSidePanelOpen(true);
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const handleOpenUserMenu = t9;
  let t10;
  if ($[11] !== projectBrowser || $[12] !== router) {
    t10 = () => {
      if (projectBrowser) {
        projectBrowser.openBrowser();
        return;
      }
      router.push("/playground?view=browse");
    };
    $[11] = projectBrowser;
    $[12] = router;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const handleOpenProjectBrowser = t10;
  const toolbarHeight = useMobilePlayground ? MOBILE_APPBAR_HEIGHT : 56;
  const isCompact = useCompactNav;
  let t11;
  if ($[14] !== isSidePanelOpen) {
    t11 = <SidePanel isOpen={isSidePanelOpen} setIsOpen={setIsSidePanelOpen} />;
    $[14] = isSidePanelOpen;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const T0 = AppBar;
  const t12 = "app-bar";
  const t13 = 0;
  const t14 = "transparent";
  const t15 = isScrolled ? "blur(20px)" : "blur(0px)";
  let t16;
  if ($[16] !== isScrolled || $[17] !== theme.appDesign) {
    t16 = isScrolled ? `1px solid ${alpha(theme.appDesign.outline, 0.18)}` : "1px solid transparent";
    $[16] = isScrolled;
    $[17] = theme.appDesign;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  let t17;
  if ($[19] !== t15 || $[20] !== t16 || $[21] !== theme.palette.text.primary) {
    t17 = {
      paddingTop: "env(safe-area-inset-top, 0px)",
      transition: "all .2s",
      backdropFilter: t15,
      backgroundColor: "transparent",
      boxShadow: "none",
      borderBottom: t16,
      color: theme.palette.text.primary
    };
    $[19] = t15;
    $[20] = t16;
    $[21] = theme.palette.text.primary;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  const T1 = Container;
  const t18 = "xl";
  const T2 = Toolbar;
  const t19 = true;
  let t20;
  if ($[23] !== useMobilePlayground) {
    t20 = useMobilePlayground ? 1 : {
      xs: 1.25,
      md: 0
    };
    $[23] = useMobilePlayground;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] !== t20 || $[26] !== toolbarHeight) {
    t21 = {
      height: toolbarHeight,
      px: t20
    };
    $[25] = t20;
    $[26] = toolbarHeight;
    $[27] = t21;
  } else {
    t21 = $[27];
  }
  let t22;
  if ($[28] !== LL) {
    t22 = LL.DSTRUCT_LOGO();
    $[28] = LL;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  const t23 = !useCompactNav;
  const t24 = isCompact ? 24 : 32;
  let t25;
  if ($[30] !== t22 || $[31] !== t23 || $[32] !== t24) {
    t25 = <LogoBrand alt={t22} showName={t23} logoSize={t24} />;
    $[30] = t22;
    $[31] = t23;
    $[32] = t24;
    $[33] = t25;
  } else {
    t25 = $[33];
  }
  const t26 = useCompactNav && <Box sx={{
    flexGrow: 1,
    display: "flex"
  }}><IconButton size="large" aria-label={LL.CURRENT_USER_ACCOUNT()} aria-controls="menu-appbar" aria-haspopup="true" data-testid="app-bar-compact-nav" onClick={handleOpenNavMenu} color="inherit"><MenuIcon /></IconButton><Menu id="menu-appbar" anchorEl={anchorElNav} anchorOrigin={{
      vertical: "bottom",
      horizontal: "left"
    }} keepMounted={true} transformOrigin={{
      vertical: "top",
      horizontal: "left"
    }} open={Boolean(anchorElNav)} onClose={handleCloseNavMenu} sx={{
      display: "block"
    }}>{pages.map(page => <MenuItem key={page.href} component={Link} href={page.href} onClick={handleCloseNavMenu}><Typography sx={{
          textAlign: "center"
        }}>{page.name}</Typography></MenuItem>)}</Menu></Box>;
  const t27 = !useCompactNav && <Box sx={{
    flexGrow: 1,
    gap: 1,
    display: "flex"
  }}>{pages.map(page_0 => <Button key={page_0.href} component={Link} href={page_0.href} data-testid={page_0.href === "/playground" ? "app-bar-nav-playground" : "app-bar-nav-daily"} color="inherit" sx={{
      backgroundColor: page_0.href && currentPath.startsWith(page_0.href) ? alpha(theme.appDesign.surfaceHighest, 0.9) : "transparent",
      border: page_0.href && currentPath.startsWith(page_0.href) ? `1px solid ${alpha(theme.appDesign.outline, 0.22)}` : "1px solid transparent",
      "&:hover": {
        backgroundColor: alpha(theme.appDesign.surfaceHigh, 0.82),
        borderColor: alpha(theme.appDesign.outline, 0.18)
      }
    }}>{page_0.name}</Button>)}</Box>;
  let t28;
  if ($[34] !== useCompactNav) {
    t28 = useCompactNav && <Box sx={{
      flex: 1
    }} />;
    $[34] = useCompactNav;
    $[35] = t28;
  } else {
    t28 = $[35];
  }
  const t29 = isCompact ? 0.5 : 1;
  let t30;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = {
      alignItems: "center",
      flexGrow: 0
    };
    $[36] = t30;
  } else {
    t30 = $[36];
  }
  let t31;
  if ($[37] !== LL || $[38] !== currentView || $[39] !== handleOpenProjectBrowser || $[40] !== isCompact || $[41] !== useMobilePlayground) {
    t31 = (!useMobilePlayground || currentView !== "browse") && <Tooltip title={LL.PROJECT_BROWSER()} arrow={true}><IconButton size={isCompact ? "small" : "medium"} onClick={handleOpenProjectBrowser} color="inherit" aria-label={LL.PROJECT_BROWSER()}><FolderOpen fontSize={isCompact ? "small" : "medium"} /></IconButton></Tooltip>;
    $[37] = LL;
    $[38] = currentView;
    $[39] = handleOpenProjectBrowser;
    $[40] = isCompact;
    $[41] = useMobilePlayground;
    $[42] = t31;
  } else {
    t31 = $[42];
  }
  let t32;
  if ($[43] !== LL || $[44] !== handleOpenUserMenu || $[45] !== handleSignIn || $[46] !== hasMounted || $[47] !== isCompact || $[48] !== session || $[49] !== useMobilePlayground) {
    t32 = useMobilePlayground && !hasMounted || session.status === "loading" ? <Skeleton variant="circular" animation="wave" height={isCompact ? 32 : 40} width={isCompact ? 32 : 40} /> : <>{session.status === "unauthenticated" && <Button title={LL.SIGN_IN()} color="inherit" size={isCompact ? "small" : "medium"} onClick={handleSignIn} sx={isCompact ? {
        fontSize: "0.75rem",
        minWidth: "auto",
        px: 1
      } : undefined}>{LL.SIGN_IN()}</Button>}<Tooltip title={LL.OPEN_OPTIONS()} arrow={true}><IconButton size={isCompact ? "small" : "medium"} onClick={handleOpenUserMenu}>{session.data ? <Avatar sx={isCompact ? {
            width: 28,
            height: 28
          } : undefined}><Image src={getImageUrl(session.data.user.bucketImage || AVATAR_PLACEHOLDER)} alt={`${session.data.user.name} avatar`} referrerPolicy="no-referrer" crossOrigin="anonymous" width={isCompact ? 28 : 40} height={isCompact ? 28 : 40} /></Avatar> : <Settings fontSize={isCompact ? "small" : "medium"} />}</IconButton></Tooltip></>;
    $[43] = LL;
    $[44] = handleOpenUserMenu;
    $[45] = handleSignIn;
    $[46] = hasMounted;
    $[47] = isCompact;
    $[48] = session;
    $[49] = useMobilePlayground;
    $[50] = t32;
  } else {
    t32 = $[50];
  }
  let t33;
  if ($[51] !== t29 || $[52] !== t31 || $[53] !== t32) {
    t33 = <Stack direction="row" spacing={t29} sx={t30}>{t31}{t32}</Stack>;
    $[51] = t29;
    $[52] = t31;
    $[53] = t32;
    $[54] = t33;
  } else {
    t33 = $[54];
  }
  let t34;
  if ($[55] !== T2 || $[56] !== t21 || $[57] !== t25 || $[58] !== t26 || $[59] !== t27 || $[60] !== t28 || $[61] !== t33 || $[62] !== toolbarVariant) {
    t34 = <T2 disableGutters={t19} variant={toolbarVariant} sx={t21}>{t25}{t26}{t27}{t28}{t33}</T2>;
    $[55] = T2;
    $[56] = t21;
    $[57] = t25;
    $[58] = t26;
    $[59] = t27;
    $[60] = t28;
    $[61] = t33;
    $[62] = toolbarVariant;
    $[63] = t34;
  } else {
    t34 = $[63];
  }
  let t35;
  if ($[64] !== T1 || $[65] !== t34 || $[66] !== useMobilePlayground) {
    t35 = <T1 maxWidth={t18} disableGutters={useMobilePlayground}>{t34}</T1>;
    $[64] = T1;
    $[65] = t34;
    $[66] = useMobilePlayground;
    $[67] = t35;
  } else {
    t35 = $[67];
  }
  let t36;
  if ($[68] !== T0 || $[69] !== appBarVariant || $[70] !== position || $[71] !== t17 || $[72] !== t35) {
    t36 = <T0 data-testid={t12} position={position} elevation={t13} variant={appBarVariant} color={t14} sx={t17}>{t35}</T0>;
    $[68] = T0;
    $[69] = appBarVariant;
    $[70] = position;
    $[71] = t17;
    $[72] = t35;
    $[73] = t36;
  } else {
    t36 = $[73];
  }
  let t37;
  if ($[74] !== t11 || $[75] !== t36) {
    t37 = <>{t11}{t36}</>;
    $[74] = t11;
    $[75] = t36;
    $[76] = t37;
  } else {
    t37 = $[76];
  }
  return t37;
};